import React, { useMemo, useState } from "react";
import { Box, Stack, Typography, Button, Tooltip, IconButton } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StarIcon from "@mui/icons-material/Star";
import TemplateSelector from "./TemplateSelector";
import { useTemplateLibrary } from "./useTemplateLibrary";
import {
  IEmailTemplate, CommunicationTemplateType,
  renderTemplateForLead,
} from "../../Api/emailTemplatesApi";

const NAVY = "#0F172A";
const CHAMPAGNE = "#C9A961";
const CHAMPAGNE_PALE = "#FBF7EE";
const HAIRLINE = "rgba(10,14,26,0.06)";

/** Shape passed back to the parent when a template is applied. The
 *  parent surface decides which fields it consumes — Email Hub uses
 *  both subject + body, Quick Actions use only body. */
export interface AppliedCommunicationTemplate {
  /** Template id used (for tracking on save). */
  template_id: number;
  /** Subject after variable resolution. Empty for non-email types. */
  subject: string;
  /** Body after variable resolution. */
  body: string;
}

export interface CommunicationTemplateToolbarProps {
  /** Which kind of template to filter. Drives the picker list, the
   *  quick chips, and the favorites/recents storage scope. */
  templateType: CommunicationTemplateType;
  /** Optional lead context — drives variable resolution via
   *  /render-for-lead so {{tokens}} land already-substituted in the
   *  destination field. */
  leadId?: number | null;
  /** True when the parent's body field has non-empty content — gates
   *  the "Replace existing draft?" confirmation. */
  hasExistingBody: boolean;
  /** Called when the rep applies a template. The parent should set
   *  the appropriate field(s) and persist template_id alongside the
   *  next save. */
  onApplyTemplate: (applied: AppliedCommunicationTemplate) => void;
  /** Optional override for the "Use Template" button label. Quick
   *  Actions surfaces show "Use Snippet" since the underlying notes
   *  field doesn't carry a subject. */
  buttonLabel?: string;
  /** Optional inline label that appears as a subtle "QUICK:" prefix
   *  before the chip row. Defaults to "QUICK". */
  quickLabel?: string;
}

/**
 * Generic Communication Template toolbar — sits above the body /
 * notes field on any surface that can apply a saved template.
 *
 *   [Use Template ▼]   QUICK · No Answer · Voicemail · Interested
 *
 * Same shape as EmailComposerToolbar but type-aware: the templateType
 * prop narrows the loaded library so a call-log toolbar never surfaces
 * email templates and vice versa.
 *
 * Variable resolution happens server-side via /render-for-lead when
 * a lead is in scope. The renderer is content-agnostic — it just
 * substitutes {{tokens}} regardless of whether the body is HTML or
 * plain text — so the same toolbar works for both Quill bodies (email)
 * and plain-text bodies (call/note/meeting).
 *
 * Phase 3 will add an `✨ AI Assist` button next to "Use Template".
 */
const CommunicationTemplateToolbar: React.FC<CommunicationTemplateToolbarProps> = ({
  templateType, leadId, hasExistingBody, onApplyTemplate,
  buttonLabel, quickLabel,
}) => {
  const lib = useTemplateLibrary({ type: templateType });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [applying, setApplying]     = useState(false);

  // Map of category → default-or-first active template, used by the
  // quick chips so one click applies the right template per category.
  // Templates flagged is_default win over the first-loaded one.
  const chipTemplateByCategory = useMemo(() => {
    const m = new Map<string, IEmailTemplate>();
    for (const t of lib.templates) {
      if (!t.category) continue;
      const existing = m.get(t.category);
      if (!existing) m.set(t.category, t);
      else if (t.is_default && !existing.is_default) m.set(t.category, t);
    }
    return m;
  }, [lib.templates]);

  // Quick chips render in category order. The category list already
  // comes type-scoped from useTemplateLibrary.
  const quickChips = useMemo(() => {
    return lib.categories
      .map(cat => ({ key: cat.value, label: cat.label, template: chipTemplateByCategory.get(cat.value) }))
      .filter(c => c.template !== undefined);
  }, [lib.categories, chipTemplateByCategory]);

  const applyTemplate = async (template: IEmailTemplate) => {
    if (hasExistingBody) {
      const ok = window.confirm(`Replace the current draft with template "${template.name}"?`);
      if (!ok) return;
    }
    setApplying(true);
    try {
      let subjectOut = template.subject;
      let bodyOut    = template.body;
      // When we have a lead, render with the resolver so {{tokens}}
      // land already-substituted. Same code path the email toolbar
      // uses — the resolver doesn't care that the body might be plain
      // text rather than HTML.
      if (leadId) {
        try {
          const rendered = await renderTemplateForLead(template.id, leadId);
          subjectOut = rendered.subject;
          bodyOut    = rendered.body;
        } catch {
          // Fall back to the raw template — better than blocking the
          // workflow on a resolver hiccup. Unresolved {{tokens}} are
          // visibly present so the rep can spot them.
        }
      }
      lib.markUsed(template.id);
      onApplyTemplate({ template_id: template.id, subject: subjectOut, body: bodyOut });
      setPickerOpen(false);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box sx={{
      p: 0.8, borderRadius: "8px",
      bgcolor: "#fff",
      border: `1px solid ${HAIRLINE}`,
    }}>
      <Stack direction="row" alignItems="center" gap={0.7} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          onClick={() => setPickerOpen(true)}
          startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
          disabled={lib.loading || applying}
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 11, color: NAVY,
            border: `1px solid ${HAIRLINE}`,
            bgcolor: "#fafbfc",
            px: 1.1, py: 0.25, borderRadius: "7px",
            "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" },
          }}
        >
          {buttonLabel || "Use Template"}
        </Button>

        {lib.favorites.length > 0 && (
          <Tooltip title="Open Favorites" arrow>
            <IconButton
              size="small"
              onClick={() => setPickerOpen(true)}
              sx={{
                color: CHAMPAGNE,
                border: `1px solid ${HAIRLINE}`,
                bgcolor: "#fafbfc",
                width: 26, height: 26,
                "&:hover": { bgcolor: CHAMPAGNE_PALE },
              }}
            >
              <StarIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Inline quick chips, when at least one category has a
            template loaded. Kept on the same row as the button for
            Quick Actions surfaces — vertical space is tight there. */}
        {quickChips.length > 0 && (
          <>
            <Typography sx={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", ml: 0.5 }}>
              {quickLabel || "QUICK"}:
            </Typography>
            {quickChips.map(c => (
              <Tooltip key={c.key} title={c.template!.name} arrow>
                <Box
                  role="button"
                  onClick={() => applyTemplate(c.template!)}
                  sx={{
                    cursor: "pointer", userSelect: "none",
                    px: 0.9, py: 0.25, borderRadius: "999px",
                    fontSize: 10, fontWeight: 600,
                    color: NAVY,
                    bgcolor: "#fafbfc",
                    border: `1px solid ${HAIRLINE}`,
                    "&:hover": { bgcolor: CHAMPAGNE_PALE, borderColor: CHAMPAGNE },
                  }}
                >
                  {c.label}
                </Box>
              </Tooltip>
            ))}
          </>
        )}
      </Stack>

      {pickerOpen && (
        <TemplateSelector
          open={pickerOpen}
          templates={lib.templates}
          categories={lib.categories}
          favorites={lib.favorites}
          recents={lib.recents}
          onToggleFavorite={lib.toggleFavorite}
          onApply={applyTemplate}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Box>
  );
};

export default CommunicationTemplateToolbar;
